import { createClient, type RealtimeChannel, type SupabaseClient } from "@supabase/supabase-js";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { usePostHog } from "../hooks/usePostHog";

export interface Device {
    id: string;
    user_id: string;
    device_name: string;
    status: string;
    last_seen: string;
    capabilities?: Record<string, any> | null;
    [key: string]: any;
}

// Devices write last_seen every 5 min; the margin absorbs a late write.
const LAST_SEEN_FRESH_MS = 7 * 60 * 1000;
// A row green only through last_seen is re-read this long before it would age
// out, so a newer last_seen lands before the dot turns grey.
const LAST_SEEN_REREAD_LEAD_MS = 15 * 1000;
// A row still online but already past the window may have missed one write, so
// it is re-read after about one heartbeat. Ends once the server sweep marks it
// offline.
const STALE_ONLINE_REREAD_MS = 5 * 60 * 1000;
// The status rule depends on the clock, so it is re-applied locally this often.
const STATUS_RECHECK_MS = 30 * 1000;
// A device leaves Presence before it writes its offline status, so the table
// is re-read this long after a leave rather than at once.
const LEFT_DEVICE_REREAD_DELAY_MS = 5 * 1000;

/** Online when in Presence, or when the row says online and was seen recently. */
function displayStatus(row: Device, onlineSet: Set<string>, now: number): string {
    if (onlineSet.has(row.id)) {
        return "online";
    }
    const seenRecently = now - new Date(row.last_seen).getTime() < LAST_SEEN_FRESH_MS;
    return row.status === "online" && !seenRecently ? "offline" : row.status;
}

/**
 * Derive the rows the UI shows from raw DB rows and the live Presence set.
 * Pass DB rows only, never the output of an earlier merge: it overwrites
 * `status`, and the rule needs the row's own.
 *
 * Dashboard-only: the server never reads Presence — dispatch uses the DB
 * status alone — so this view can show a dead device offline before dispatch
 * does.
 */
export function mergeStatus(dbRows: Device[], onlineSet: Set<string>, now: number): Device[] {
    return dbRows.map((d) => {
        const status = displayStatus(d, onlineSet, now);
        return status === d.status
            ? d
            : {
                  ...d,
                  status,
              };
    });
}

interface McpInfo {
    supabaseUrl: string;
    supabasePublishableKey: string;
    [key: string]: any;
}

interface SupabaseContextType {
    supabaseClient: SupabaseClient | null;
    mcpInfo: McpInfo | null;
    loading: boolean;
    error: string | null;

    // Device Management
    devices: Device[];
    devicesLoading: boolean;
    /**
     * True once a device read has succeeded for the signed in user. Until then an empty `devices`
     * means "unknown", not "no devices": the list starts empty, and a failed read leaves it empty.
     */
    devicesKnown: boolean;
    revokeDevice: (deviceId: string) => Promise<{
        success: boolean;
        message: string;
    }>;
    revokeAllDevices: () => Promise<{
        success: boolean;
        message: string;
        errors?: any[];
    }>;
    revokingId: string | null;
    revokingAll: boolean;
    /** Manual refresh. Resolves null when the fetch FAILED (vs [] = no devices). */
    refreshDevices: () => Promise<Device[] | null>;
}

const SupabaseContext = createContext<SupabaseContextType | null>(null);

export function useSupabase() {
    const context = useContext(SupabaseContext);
    if (!context) {
        throw new Error("useSupabase must be used within SupabaseProvider");
    }
    return context;
}

interface SupabaseProviderProps {
    children: ReactNode;
    accessToken?: string | null;
    userId?: string | null;
    userEmail?: string | null; // Added email for PostHog tracking
}

export function SupabaseProvider({ children, accessToken, userId, userEmail }: SupabaseProviderProps) {
    const [supabaseClient, setSupabaseClient] = useState<SupabaseClient | null>(null);
    const [mcpInfo, setMcpInfo] = useState<McpInfo | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const { track } = usePostHog();

    // Device State
    const [devices, setDevices] = useState<Device[]>([]);
    const [devicesLoading, setDevicesLoading] = useState<boolean>(false);
    // The user whose device read last succeeded. Keyed by user, so a sign in as someone else
    // starts unknown again without a reset.
    const [devicesKnownFor, setDevicesKnownFor] = useState<string | null>(null);
    const [revokingId, setRevokingId] = useState<string | null>(null);
    const [revokingAll, setRevokingAll] = useState<boolean>(false);

    // Live presence: device ids currently tracked in the user channel. Refs (not
    // state) so refetches and presence handlers always merge against the
    // latest set without re-running the subscription effect.
    const onlineSetRef = useRef<Set<string>>(new Set());
    // Raw rows from the last table read. `devices` is always derived from these,
    // and presence handlers read the roster here rather than inside a setState
    // updater (updaters must be pure; React StrictMode double-invokes them).
    const dbRowsRef = useRef<Device[]>([]);

    // Every write of the device list goes through here, so the raw rows and
    // the derived list never disagree.
    const showRows = useCallback((rows: Device[]) => {
        dbRowsRef.current = rows;
        setDevices(mergeStatus(rows, onlineSetRef.current, Date.now()));
    }, []);

    // Initialize base Supabase client (once) - for unauthenticated operations
    useEffect(() => {
        async function initSupabase() {
            try {
                // Fetch MCP server info
                const response = await fetch(`${import.meta.env.VITE_MCP_URL}/api/mcp-info`);
                if (!response.ok) {
                    throw new Error("Failed to fetch MCP info");
                }

                const info = await response.json();
                setMcpInfo(info);

                // Initialize base Supabase client (no session)
                // CRITICAL: Disable auto-refresh since we use backend-managed tokens
                const client = createClient(info.supabaseUrl, info.supabasePublishableKey, {
                    auth: {
                        persistSession: false,
                        autoRefreshToken: false, // Don't try to refresh - we handle via backend
                        storage: undefined, // Explicitly disable storage for base client
                        storageKey: "sb-base-client", // Unique key to avoid conflicts
                    },
                });
                setSupabaseClient(client);
            } catch (err: any) {
                console.error("Failed to initialize Supabase:", err);
                setError(err.message || "Unknown error");
            } finally {
                setLoading(false);
            }
        }

        initSupabase();
    }, []);

    // Create authenticated user client whenever we have credentials
    // This is a SEPARATE client instance with its own session
    // Manage authenticated session on the single client
    useEffect(() => {
        if (!supabaseClient || !accessToken || !userId) {
            if (supabaseClient && !accessToken) {
                // Optionally sign out if token is removed but client exists
                // supabaseClient.auth.signOut();
                showRows([]);
            }
            return;
        }

        const updateSession = async () => {
            // Stamp the Realtime socket FIRST and unconditionally. This used to sit in
            // the `else` of setSession(), so any setSession failure left the socket on
            // the publishable key — anon — and migration 008 denied `user:<uid>` forever.
            supabaseClient.realtime.setAuth(accessToken);

            // setSession authenticates the PostgREST side; without it `.from()` runs as
            // anon and RLS returns ZERO ROWS with no error (see fetchUserDevices).
            // refresh_token is the access token because the browser never holds a real
            // one — harmless while the token is valid, but setSession does try to use it
            // when the token is already expired, and that always fails.
            const { error: sessionError } = await supabaseClient.auth.setSession({
                access_token: accessToken,
                refresh_token: accessToken,
            });

            if (sessionError) {
                // No longer fatal for Realtime (setAuth already ran), but PostgREST is
                // now anonymous — fetchUserDevices must not trust an empty read.
                console.error("❌ [SUPABASE] Failed to set user session:", sessionError.message);
            }
        };

        updateSession();
    }, [
        supabaseClient,
        accessToken,
        userId,
    ]);

    // --- Device Management Logic ---

    const fetchUserDevices = useCallback(async () => {
        // null, not [] — this is the same "we don't know" case as a failed fetch,
        // and returning [] here would let a refetch blank the roster exactly as
        // the catch below used to.
        if (!supabaseClient || !accessToken || !userId) {
            return null;
        }

        try {
            // NOTE: We use supabaseClient which has the session
            const { data, error } = await supabaseClient.from("mcp_devices").select("*").eq("user_id", userId).order("last_seen", {
                ascending: false,
            });

            if (error) {
                throw error;
            }

            const rows = (data as Device[]) || [];

            // An empty result is ambiguous: either the user really has no devices, or
            // the session failed to attach and RLS hid every row (anon => auth.uid()
            // NULL => no match => [] with error null). Trusting it blanked the roster
            // and made devices "disappear" until re-login. Only believe [] if the
            // client actually holds a session.
            if (rows.length === 0) {
                const { data: sessionData } = await supabaseClient.auth.getSession();
                if (!sessionData?.session) {
                    console.error("❌ [DEVICES] Empty device read on an unauthenticated client — ignoring");
                    return null;
                }
            }

            return rows;
        } catch (error) {
            // Return null (not []) so callers can tell "fetch failed" from "no
            // devices" — writing [] on a transient 401/network blip would blank the
            // roster.
            console.error("Error fetching devices:", error);
            return null;
        }
    }, [
        supabaseClient,
        accessToken,
        userId,
    ]);

    const revokeDevice = useCallback(
        async (deviceId: string) => {
            if (!accessToken) {
                return {
                    success: false,
                    message: "Not authenticated",
                };
            }

            setRevokingId(deviceId);
            try {
                const response = await fetch("/device/revoke", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({
                        device_id: deviceId,
                    }),
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.message || data.error || "Failed to revoke device");
                }

                // Refresh list immediately - Optimistic-style update (post-success)
                // We remove the device from state instead of re-fetching to avoid race conditions with Realtime
                showRows(dbRowsRef.current.filter((d) => d.id !== deviceId));

                // Track successful device revocation
                track("device_revoke_completed", {
                    user_id: userId,
                    email: userEmail,
                    device_id: deviceId,
                    remaining_devices: devices.length - 1,
                });

                return {
                    success: true,
                    message: data.message,
                };
            } catch (error: unknown) {
                console.error("Revoke device failed:", error);
                const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";

                // Track device revocation failure
                track("device_revoke_failed", {
                    user_id: userId,
                    email: userEmail,
                    device_id: deviceId,
                    error_message: errorMessage,
                });

                return {
                    success: false,
                    message: errorMessage,
                };
            } finally {
                setRevokingId(null);
            }
        },
        [
            accessToken,
            fetchUserDevices,
            track,
            userId,
            userEmail,
        ],
    );

    const revokeAllDevices = useCallback(async () => {
        if (!accessToken) {
            return {
                success: false,
                message: "Not authenticated",
            };
        }

        const deviceCountBeforeRevoke = devices.length;
        setRevokingAll(true);
        try {
            const response = await fetch("/device/revoke-all", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            const data = await response.json();

            if (!response.ok) {
                // A partial failure still revoked some devices — drop those from the list.
                if (Array.isArray(data.revokedIds) && data.revokedIds.length > 0) {
                    const revoked = new Set<string>(data.revokedIds);
                    showRows(dbRowsRef.current.filter((d) => !revoked.has(d.id)));
                }
                throw new Error(data.message || data.error || "Failed to revoke all devices");
            }

            // Refresh list immediately - Optimistic-style update (post-success)
            showRows([]);

            // Track successful bulk revocation
            track("device_revoke_all_completed", {
                user_id: userId,
                email: userEmail,
                revoked_count: deviceCountBeforeRevoke,
                failed_count: data.errors?.length || 0,
            });

            return {
                success: true,
                message: data.message,
                errors: data.errors as
                    | Array<{
                          deviceId: string;
                          error: string;
                      }>
                    | undefined,
            };
        } catch (error: unknown) {
            console.error("Revoke all devices failed:", error);
            const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";

            // Track bulk revocation failure
            track("device_revoke_all_failed", {
                user_id: userId,
                email: userEmail,
                error_message: errorMessage,
            });

            return {
                success: false,
                message: errorMessage,
            };
        } finally {
            setRevokingAll(false);
        }
    }, [
        accessToken,
        devices.length,
        track,
        userId,
        userEmail,
    ]);

    // Live device status via Presence on the private user channel.
    // mcp_devices left the realtime publication (migration 007), so the old
    // postgres_changes subscription would receive nothing — instead:
    //   - devices announce themselves via Presence on `user:{userId}` (status
    //     flips within seconds)
    //   - the table is re-read when presence shows a device the roster can't
    //     place, when a known device leaves (see onPresenceChange), and just
    //     before a row green only through last_seen would age out (see
    //     scheduleLastSeenReread); otherwise row details show the last read
    useEffect(() => {
        let channel: RealtimeChannel | null = null;
        let retryTimer: ReturnType<typeof setTimeout> | null = null;
        let leftDeviceRereadTimer: ReturnType<typeof setTimeout> | null = null;
        let lastSeenRereadTimer: ReturnType<typeof setTimeout> | null = null;
        let lastSeenRereadAt = Number.POSITIVE_INFINITY;
        let retryAttempt = 0; // exponential backoff for re-join (reset on SUBSCRIBED)
        let isActive = true;

        // A device outside presence keeps writing last_seen, but this page only
        // sees it on a read. Re-read just before the first such row would age
        // out, or after STALE_ONLINE_REREAD_MS for a row already past it. A row
        // the server has marked offline costs no reads.
        const scheduleLastSeenReread = () => {
            const now = Date.now();
            let rereadAt = Number.POSITIVE_INFINITY;
            for (const d of dbRowsRef.current) {
                if (d.status !== "online" || onlineSetRef.current.has(d.id)) {
                    continue;
                }
                const at = new Date(d.last_seen).getTime() + LAST_SEEN_FRESH_MS - LAST_SEEN_REREAD_LEAD_MS;
                const next = at > now ? at : now + STALE_ONLINE_REREAD_MS;
                if (next < rereadAt) {
                    rereadAt = next;
                }
            }
            // An armed read that comes no later stays, so the 30 s recheck can't
            // keep pushing a stale row's read back.
            if (lastSeenRereadTimer && lastSeenRereadAt <= rereadAt) {
                return;
            }
            if (lastSeenRereadTimer) {
                clearTimeout(lastSeenRereadTimer);
                lastSeenRereadTimer = null;
            }
            if (rereadAt === Number.POSITIVE_INFINITY) {
                return;
            }
            lastSeenRereadAt = rereadAt;
            // Capped so a last_seen far in the future can't overflow setTimeout.
            lastSeenRereadTimer = setTimeout(
                () => {
                    lastSeenRereadTimer = null;
                    refreshAndMerge();
                },
                Math.min(rereadAt - now, LAST_SEEN_FRESH_MS),
            );
        };

        // Before the first presence sync the online set is empty, so rows follow
        // the last_seen rule alone.
        const applyMerge = (rows: Device[]) => {
            if (!isActive) {
                return;
            }
            showRows(rows);
            scheduleLastSeenReread();
        };

        // Re-apply the status rule to the rows already read. No network.
        const remerge = () => {
            if (!isActive) {
                return;
            }
            const next = mergeStatus(dbRowsRef.current, onlineSetRef.current, Date.now());
            setDevices((prev) =>
                prev.length === next.length && prev.every((d, i) => d.id === next[i].id && d.status === next[i].status) ? prev : next,
            );
            scheduleLastSeenReread();
        };

        const refreshAndMerge = async () => {
            const rows = await fetchUserDevices();
            // null = the fetch failed; keep the roster we already have rather than
            // blanking the list.
            if (rows === null) {
                return;
            }
            applyMerge(rows);
            setDevicesKnownFor(userId ?? null);
        };

        const onPresenceChange = () => {
            if (!isActive || !channel) {
                return;
            }
            const previous = onlineSetRef.current;
            // Presence keys are device ids (devices track with key = device_id).
            const online = new Set(Object.keys(channel.presenceState()));
            onlineSetRef.current = online;

            const known = new Set(dbRowsRef.current.map((d) => d.id));
            // A device that left falls back to its row, which may be minutes old.
            // Re-read once its offline write has had time to land; a later leave
            // pushes the read back.
            if (
                [
                    ...previous,
                ].some((id) => known.has(id) && !online.has(id))
            ) {
                if (leftDeviceRereadTimer) {
                    clearTimeout(leftDeviceRereadTimer);
                }
                leftDeviceRereadTimer = setTimeout(() => {
                    leftDeviceRereadTimer = null;
                    refreshAndMerge();
                }, LEFT_DEVICE_REREAD_DELAY_MS);
            }
            // A device we don't have a row for just came online (fresh registration).
            // A pending re-read above will pick it up too.
            if (
                !leftDeviceRereadTimer &&
                [
                    ...online,
                ].some((id) => !known.has(id))
            ) {
                refreshAndMerge();
                return;
            }
            remerge();
        };

        const joinChannel = () => {
            if (!isActive || !supabaseClient || !userId) {
                return;
            }

            // Observer only — the dashboard must NEVER track() here, or it would
            // show up as a "device" in every presence reader. Sync only: it fires
            // once a state or diff is fully applied; join and leave can fire
            // while presenceState() is mid-update.
            const myChannel = supabaseClient
                .channel(`user:${userId}`, {
                    config: {
                        private: true,
                    },
                })
                .on(
                    "presence",
                    {
                        event: "sync",
                    },
                    onPresenceChange,
                );
            channel = myChannel;

            myChannel.subscribe((status, err) => {
                // removeChannel() fires CLOSED synchronously on the channel being
                // removed — events from a channel we already replaced/detached must
                // be ignored, or the retry below re-removes the replacement forever
                // (same bug class as the server-side idle-release recursion).
                if (channel !== myChannel) {
                    return;
                }

                if (status === "SUBSCRIBED") {
                    // Healthy again — reset the backoff AND cancel any pending retry.
                    // supabase-js rejoins on its own; if it wins the race, a still-armed
                    // timer would later tear down this healthy channel and reconnect it
                    // for no reason.
                    retryAttempt = 0;
                    if (retryTimer) {
                        clearTimeout(retryTimer);
                        retryTimer = null;
                    }
                    return;
                }

                if (status === "CHANNEL_ERROR" || status === "TIMED_OUT" || status === "CLOSED") {
                    if (err) {
                        console.error("❌ [DEVICES] Presence channel error:", err);
                    }
                    if (!isActive) {
                        return;
                    }

                    // Detach NOW, not inside the delayed retry below: realtime-js runs
                    // its own auto-rejoin timer underneath us, settling around 10s —
                    // faster than our backoff can ever mature — and every one of those
                    // internal retries re-fires this same callback, endlessly resetting
                    // retryTimer before it completes. The channel was never actually
                    // torn down and rebuilt with a fresh token; it just retried forever
                    // on whatever (possibly stale) auth it started with. Detaching here
                    // makes `channel !== myChannel` above ignore every further event
                    // from this dead channel, so our backoff finally gets to run.
                    const stale = channel;
                    channel = null;
                    if (stale) {
                        supabaseClient.removeChannel(stale);
                    }

                    // Re-join with EXPONENTIAL BACKOFF: 5s, 10s, 20s, ... capped at
                    // 5 min; resets on a successful SUBSCRIBED. Transient blips still
                    // recover on the first 5s retry; a PERSISTENT failure (e.g. a
                    // stale token the auth poll never manages to refresh) settles at
                    // one join attempt per 5 min instead of hammering Unauthorized
                    // and burning Realtime quota.
                    const delay = Math.min(300_000, 5_000 * 2 ** retryAttempt);
                    retryAttempt++;
                    if (retryTimer) {
                        clearTimeout(retryTimer);
                    }
                    retryTimer = setTimeout(() => {
                        if (!isActive || !supabaseClient) {
                            return;
                        }
                        joinChannel();
                    }, delay);
                }
            });
        };

        const init = async () => {
            if (!supabaseClient || !accessToken) {
                return;
            }

            setDevicesLoading(true);
            const deviceList = await fetchUserDevices();

            if (isActive) {
                // Via applyMerge so the invariant above holds for every DB-sourced write.
                applyMerge(deviceList ?? []);
                // Only a successful read makes the count known. A failed read keeps the empty
                // roster above but must not read as "no devices" (the upgrade account check).
                if (deviceList !== null) {
                    setDevicesKnownFor(userId ?? null);
                }
                setDevicesLoading(false);
                joinChannel();
            }
        };

        // Only run init if we have a supabaseClient (which means we have a session)
        if (supabaseClient) {
            init();
        }

        // The rule depends on the clock: a row ageing past the last_seen window
        // must turn grey without a new event.
        const recheckTimer = setInterval(remerge, STATUS_RECHECK_MS);

        return () => {
            isActive = false;
            clearInterval(recheckTimer);
            if (retryTimer) {
                clearTimeout(retryTimer);
            }
            if (leftDeviceRereadTimer) {
                clearTimeout(leftDeviceRereadTimer);
            }
            if (lastSeenRereadTimer) {
                clearTimeout(lastSeenRereadTimer);
            }
            onlineSetRef.current = new Set();
            if (channel && supabaseClient) {
                supabaseClient.removeChannel(channel);
            }
        };
    }, [
        supabaseClient,
        userId,
        accessToken,
        fetchUserDevices,
    ]);

    return (
        <SupabaseContext.Provider
            value={{
                supabaseClient,
                mcpInfo,
                loading,
                error,
                devices,
                devicesLoading,
                devicesKnown: Boolean(userId) && devicesKnownFor === userId,
                revokeDevice,
                revokeAllDevices,
                revokingId,
                revokingAll,
                refreshDevices: fetchUserDevices,
            }}
        >
            {children}
        </SupabaseContext.Provider>
    );
}
